import classNames from 'classnames/bind';
import { format, isValid } from 'date-fns';
import React, { useEffect, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
// eslint-disable-next-line import/no-named-default
import { default as CustomDatePicker } from './DatePicker';
import {
  DATE_FORMATS,
  FILTER_RELATIVE_DATE_RANGE,
  INPUT_MASK,
  RANGE_INPUT_MASK,
  RANGE_SEPARATOR,
  RELATIVE_DATE_RANGE_LABELS,
} from './DateRange.constants';
import { parseDateRangeString } from './DateRange.logic';
import styles from './DateRange.module.scss';
import { DateType } from './DateRange.types';
import MaskedDateInput from './MaskedDateInput';
import Button, { ButtonMode, ButtonSize } from '../Button/Button';
import Icon, { ICONS } from '../Icon';
import Divider from '../Divider';
import Popup from '../Popup';
import usePreviousValue from '../../hooks/usePreviousValue';
import useWindowResizeListener from '../../hooks/useWindowResizeListener';
import { isBeforeOrEqualIgnoringTime } from './dateRangeUtils';

const cx = classNames.bind(styles);

interface DateRangeProps {
  label?: string;
  startDate: DateType;
  endDate: DateType;
  relativeRange: FILTER_RELATIVE_DATE_RANGE | null;
  onChange: (e: {
    startDate?: DateType;
    endDate?: DateType;
    relativeRange?: FILTER_RELATIVE_DATE_RANGE | null;
  }) => void;
  resetInput: number;
  error: boolean;
  setError: (data: boolean) => void;
}

const DateRange = ({
  label,
  startDate,
  endDate,
  relativeRange,
  onChange,
  resetInput,
  error,
  setError,
}: DateRangeProps): React.ReactElement => {
  const [custom, setCustom] = useState<boolean>(false);
  const [open, setOpen] = useState(false);
  const preventPopupClose = useRef(false);
  const [rawValue, setRawValue] = useState<string>(RANGE_INPUT_MASK);
  const previousResetInput = usePreviousValue(resetInput);
  const maskedDateInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (resetInput !== 0) {
      setRawValue(RANGE_INPUT_MASK);
    }
  }, [resetInput]);

  useEffect(() => {
    const shouldUseMask = resetInput !== previousResetInput;
    let start;
    let end;
    const isStartValid = startDate && isValid(startDate);
    const isEndValid = endDate && isValid(endDate);

    if (isStartValid) {
      start = format(startDate, DATE_FORMATS.MONTH_DAY_YEAR);
    } else {
      start = shouldUseMask || isEndValid ? INPUT_MASK : rawValue.slice(0, 8);
    }

    if (isEndValid) {
      end = format(endDate, DATE_FORMATS.MONTH_DAY_YEAR);
    } else {
      end = shouldUseMask || isStartValid ? INPUT_MASK : rawValue.slice(11);
    }

    const value = `${start}${RANGE_SEPARATOR}${end}`;
    setRawValue(value);
  }, [startDate, endDate]);

  useEffect(() => {
    if (!open) {
      if (relativeRange || (startDate === null && endDate === null && rawValue === RANGE_INPUT_MASK)) {
        setCustom(false);
      } else if (startDate !== null || endDate !== null) {
        setCustom(true);
      }
      validateInput();
    }
  }, [open, startDate, endDate, relativeRange, rawValue]);

  const onResize = () => {
    if (!isMobile) {
      setOpen(false);
      maskedDateInputRef?.current?.blur();
    }
  };

  useWindowResizeListener(onResize);

  const onRelativeRangeSelect = (range: FILTER_RELATIVE_DATE_RANGE | null) => {
    onChange({ relativeRange: range });
    setOpen(false);
  };

  const onInputChange = (value: string) => {
    const { isStartDateValid, isEndDateValid, isRangeValid, rawStart, rawEnd } = parseDateRangeString(value);
    let newRawValue = value;
    if (isStartDateValid && isEndDateValid && !isRangeValid) {
      // Set start date to both start & end date when entered start date is after end date
      // Set end date to both start & end date when entered end date is before start date
      const [oldStart] = rawValue.split(RANGE_SEPARATOR);
      const newValue = oldStart !== rawStart ? rawStart : rawEnd;
      newRawValue = `${newValue}${RANGE_SEPARATOR}${newValue}`;
    }
    setRawValue(newRawValue);
    const { isRangeValid: newRangeIsValid, parsedStartDate, parsedEndDate } = parseDateRangeString(newRawValue);
    onChange({
      startDate: newRangeIsValid ? parsedStartDate : null,
      endDate: newRangeIsValid ? parsedEndDate : null,
    });
  };

  const onDatePickerEndSelect = (endDate: DateType) => {
    setError(false);
    const { isStartDateValid, parsedStartDate } = parseDateRangeString(rawValue);
    let start = isStartDateValid ? parsedStartDate : startDate;
    if (start && isBeforeOrEqualIgnoringTime(endDate!, start)) {
      start = endDate;
    }
    onChange({ startDate: start, endDate });
  };

  const onDatePickerStartSelect = (startDate: DateType) => {
    setError(false);
    const { isEndDateValid, parsedEndDate } = parseDateRangeString(rawValue);
    let end = isEndDateValid ? parsedEndDate : endDate;
    if (end && !isBeforeOrEqualIgnoringTime(startDate!, end)) {
      end = startDate;
    }
    onChange({ startDate, endDate: end });
  };

  const onReset = () => {
    onChange({ startDate: null, endDate: null, relativeRange: null });
  };

  const validateInput = () => {
    const { isRangeValid } = parseDateRangeString(rawValue);

    setError(!isRangeValid);
  };

  const renderReset = () => (
    <Button data-testid="date-range-reset-button" mode={ButtonMode.TERTIARY} size={ButtonSize.SMALL} onClick={onReset}>
      <Icon name={ICONS.Clear} width={12} height={12} />
      Reset
    </Button>
  );

  const renderTrigger = () => {
    if (custom) {
      return (
        <MaskedDateInput
          ref={maskedDateInputRef}
          value={rawValue}
          onInputChange={onInputChange}
          onBlur={validateInput}
          error={error}
          onFocus={() => {
            preventPopupClose.current = false;
            setOpen(true);
          }}
          isDatePickerOpen={open}
          // when clicking on caret inside masked input, that click is interpreted as document click,
          // therefore Popup onClose is being called (even with e.stopPropagation)
          // to overcome this issue, preventPopupClose is used
          onCaretMouseUp={() => {
            preventPopupClose.current = true;
          }}
          onCaretClick={() => {
            preventPopupClose.current = false;
            setOpen((prevState) => !prevState);
          }}
          open={open}
        />
      );
    }
    return (
      <div className={cx('trigger')}>
        <Button
          className={cx('button-trigger')}
          mode={ButtonMode.SECONDARY}
          size={ButtonSize.SMALL}
          dataTestId="data-range-select"
        >
          {relativeRange !== null ? RELATIVE_DATE_RANGE_LABELS[relativeRange] : label || 'Date'}
        </Button>
        <div className={cx('actions')}>
          {relativeRange !== null && (
            <Button
              className={cx('clear-button')}
              mode={ButtonMode.TERTIARY}
              onClick={(e) => {
                onRelativeRangeSelect(null);
                e.stopPropagation();
              }}
              dataTestId="date-range-reset-icon"
            >
              <Icon name={ICONS.Times} />
            </Button>
          )}
          <span className={cx('filter-caret', { rotate: open })}>
            <Icon name={ICONS.CaretDown} height={10} width={10} />
          </span>
        </div>
      </div>
    );
  };

  const renderPickers = () => {
    if (custom) {
      const { parsedStartDate, parsedEndDate } = parseDateRangeString(rawValue);
      return (
        <div className={cx('pickers-container')} data-testid="date-pickers-container">
          <Button
            mode={ButtonMode.PLAIN}
            className={cx('back-button')}
            onClick={() => setCustom(false)}
            dataTestId="date-range-back-button"
          >
            <Icon name={ICONS.ArrowLeft} height={12} width={12} />
            Standard Date Ranges
          </Button>
          <Divider />
          <div className={cx('pickers')}>
            <CustomDatePicker
              inputValue={parsedStartDate}
              selectsStart
              selected={startDate}
              startDate={startDate}
              endDate={endDate}
              onChange={onDatePickerStartSelect}
              maxDate={endDate || undefined}
              dataTestId="date-range-start-picker"
            />
            <CustomDatePicker
              inputValue={parsedEndDate}
              selectsEnd
              selected={endDate}
              startDate={startDate}
              endDate={endDate}
              onChange={onDatePickerEndSelect}
              minDate={startDate}
              dataTestId="date-range-end-picker"
            />
          </div>
        </div>
      );
    }
    return (
      <div className={cx('standard-range')} data-testid="date-range-popup">
        <div className={cx('options-wrapper')}>
          {Object.keys(FILTER_RELATIVE_DATE_RANGE).map((relativeRange) => (
            <Button
              mode={ButtonMode.PLAIN}
              className={cx('range-option')}
              dataTestId="standard-range-option"
              key={relativeRange}
              onClick={() => onRelativeRangeSelect(FILTER_RELATIVE_DATE_RANGE[relativeRange])}
            >
              {RELATIVE_DATE_RANGE_LABELS[relativeRange]}
            </Button>
          ))}
          <Button
            mode={ButtonMode.PLAIN}
            className={cx('range-option')}
            dataTestId="custom-range-option"
            onClick={() => {
              if (relativeRange === null && startDate === null && endDate === null) {
                setRawValue(RANGE_INPUT_MASK);
              }
              setCustom(true);
            }}
          >
            <span>Custom</span>
            <Icon name={ICONS.ArrowRight} height={12} width={12} />
          </Button>
        </div>
        <Divider />
        <div>{renderReset()}</div>
      </div>
    );
  };

  return (
    <div className={cx('body')}>
      <Popup
        trigger={renderTrigger()}
        on="click"
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => {
          if (!preventPopupClose.current) {
            setOpen(false);
          }
        }}
        className={cx('dropdown-date-popup')}
        position="bottom left"
      >
        {renderPickers()}
      </Popup>
    </div>
  );
};

export default DateRange;
